import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const embryoFreezing: ContentBlock<"program"> = {
  id: makeId("program-embryo-freezing"),
  kind: "program",
  tags: [
    "embryo-freezing",
    "both-partners",
    "treatment",
    "pre-cycle",
    "during-cycle",
  ],
  title: "Embryo Freezing",
  subtitle: "Create and freeze embryos — assessment through genetic testing",
  icon: "🧊",
  body: {
    duration: "3 weeks – 5 months",
    includes: [
      "Full fertility assessment for both partners",
      "Optional 90-day optimization for her and him",
      "Ovarian stimulation with monitoring",
      "Egg retrieval procedure",
      "Fertilization and embryo culture to blastocyst",
      "Optional PGT-A genetic screening of embryos",
      "Cryopreservation and annual storage",
    ],
    priceRange: { low: 15000, high: 25000 },
    currency: "USD",
    status: "coming-soon",
    forWhom: "both",
    category: "treatment",
    customizable: true,
    moduleIds: [
      makeId("module-female-hormone-baseline"),
      makeId("module-male-fertility-baseline"),
      makeId("module-female-optimization"),
      makeId("module-male-optimization"),
      makeId("module-ovarian-stimulation"),
      makeId("module-egg-retrieval"),
      makeId("module-fertilization-culture"),
      makeId("module-pgt-a"),
    ],
    optionalModuleIds: [
      makeId("module-female-optimization"),
      makeId("module-male-optimization"),
      makeId("module-pgt-a"),
    ],
    phases: [
      {
        title: "Assessment",
        description: "Understand where you both stand",
        moduleIds: [
          makeId("module-female-hormone-baseline"),
          makeId("module-male-fertility-baseline"),
        ],
        parallel: true,
      },
      {
        title: "Optimize (optional)",
        description: "90 days to prepare both partners",
        moduleIds: [
          makeId("module-female-optimization"),
          makeId("module-male-optimization"),
        ],
        parallel: true,
      },
      {
        title: "Stimulation & Retrieval",
        description: "Stimulation, retrieval, and sperm collection",
        moduleIds: [
          makeId("module-ovarian-stimulation"),
          makeId("module-egg-retrieval"),
        ],
      },
      {
        title: "Fertilization & Testing",
        description: "Embryo development and optional genetic screening",
        moduleIds: [
          makeId("module-fertilization-culture"),
          makeId("module-pgt-a"),
        ],
      },
    ],
    steps: [
      {
        title: "Both assessed",
        description:
          "Hormone panel + ultrasound for her, semen analysis for him.",
      },
      {
        title: "Optimize (optional)",
        description:
          "90 days of supplements and lifestyle changes for both partners.",
      },
      {
        title: "Stimulation & Retrieval",
        description:
          "10–14 days of stimulation, then egg retrieval and sperm collection.",
      },
      {
        title: "Fertilization & Testing",
        description:
          "Fertilization, culture to blastocyst, optional PGT-A screening, then freeze.",
      },
    ],
  },
  sources: [
    {
      title: "Planned oocyte cryopreservation outcomes and predictors",
      journal: "Fertility and Sterility",
      year: 2023,
      evidenceLevel: "cohort",
    },
  ],
};

registerBlock(embryoFreezing);

const id = makeId("program-embryo-freezing");
registerRelationship({
  from: id,
  to: makeId("module-female-hormone-baseline"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-male-fertility-baseline"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-female-optimization"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-male-optimization"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-ovarian-stimulation"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-egg-retrieval"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-fertilization-culture"),
  type: "contains",
});
registerRelationship({
  from: id,
  to: makeId("module-pgt-a"),
  type: "contains",
});
